import { Namespace, TFunction } from "i18next";
import { useAtom } from "jotai";
import { FC, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import DRStories from "@/assets/friendsWithBenefits/dr_stories.svg?react";
import { ArrowLeft } from "@/assets/icons/icons";
import { SlideShow } from "@/components/FriendsWithBenefits/SlideShow";
import { WelcomeScreenBackground } from "@/components/Layout/WelcomeScreen/Background/WelcomeScreenBackground";
import { HeroSection } from "@/components/Layout/WelcomeScreen/HeroSection/HeroSection";
import { WelcomeScreenFooter } from "@/components/Layout/WelcomeScreenFooter/WelcomeScreenFooter";
import { WelcomeFriendsWithBenefitsContent } from "@/components/Welcome/WelcomeFriendsWithBenefitsContent/WelcomeFriendsWithBenefitsContent";
import { useHandleJoinTribe } from "@/hooks/useHandleJoinTribe";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { useGetSimpleTribe } from "@/services/api";
import { accessTokenAtom, isAnonymousUserAtom } from "@/utils/atoms";
import { LogInForm } from "@/views/WelcomeScreen/LogInForm";
import { RegisterForm } from "@/views/WelcomeScreen/RegisterForm";

const WelcomeInfoSection: FC<{
  t: TFunction<Namespace, undefined, Namespace>;
  referralName: string;
  extended?: boolean;
}> = ({ t, referralName, extended }) => (
  <p className="my-4 font-semibold text-white md:text-black">
    <Trans
      i18nKey="friendsWithBenefits.welcome.youWereInvited"
      components={[<span className="text-secondary-100" key="key" />]}
      values={{
        user: referralName,
      }}
    />
    {/* Empty space infront of text is intentional */}
    {extended && ` ${t("friendsWithBenefits.welcome.referralCTA")}`}
  </p>
);

const LearnMoreSection: FC<{
  t: TFunction<Namespace, undefined, Namespace>;
}> = ({ t }) => (
  <p className="mt-10 text-white md:absolute md:bottom-10 md:max-w-[40%] md:text-black">
    <Trans
      i18nKey="friendsWithBenefits.welcome.notConvinced"
      components={[
        <a
          href={t("friendsWithBenefits.welcome.link")}
          target="_blank"
          key="0"
          className="font-semibold text-highlight-100 hover:underline smPlus:text-secondary-100"
          rel="noreferrer"
        />,
      ]}
    />
  </p>
);

const StoriesButton: FC<{
  t: TFunction<Namespace, undefined, Namespace>;
  setShowSlideShow: (show: boolean) => void;
  fullWidth?: boolean;
}> = ({ t, setShowSlideShow, fullWidth = false }) => (
  <button
    className={`mb-8 mt-8 flex items-center gap-2 rounded-xl bg-white px-4 pb-2 pt-2 shadow-primary transition-opacity hover:opacity-80 ${
      fullWidth ? "w-full" : ""
    }`}
    onClick={() => setShowSlideShow(true)}
  >
    <div className="flex h-12 w-12 items-center justify-center">
      <DRStories className="h-12 w-12" />
    </div>
    <h3 className="ml-4 text-start text-secondary-100">
      {t("friendsWithBenefits.welcome.stories")}
    </h3>
  </button>
);

export const WelcomeFriendsWithBenefits: FC = () => {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [accessToken] = useAtom(accessTokenAtom);
  const [isAnonymousUser] = useAtom(isAnonymousUserAtom);
  const joinTribe = useHandleJoinTribe();

  const [isCTA, setIsCTA] = useState(false);
  const [switchForm, setSwitchForm] = useState(true);
  const [showSlideShow, setShowSlideShow] = useState(false);

  const { tribeId, tribe2CustomerId } = useParams<{
    tribeId: string;
    tribe2CustomerId: string;
  }>();

  const { data: tribe } = useGetSimpleTribe(
    tribeId ?? "",
    tribe2CustomerId ?? "",
    {
      query: {
        enabled: true,
      },
    },
  );

  const referralName =
    tribe?.fwbFirstName || t("friendsWithBenefits.welcome.unknown");

  // Helper function to handle joining tribe with redirect
  const handleJoinTribe = (tribeIdToJoin: string) => {
    joinTribe(tribeIdToJoin, () => {
      // Navigate to group overview after successful join
      navigate("/portal/friends-with-benefits/group");
    });
  };

  // tribeId is 8 digits and tribe2CustomerId is either 9 or 10 digits, depending on int or prod system
  const isValid =
    /^\d{8}$/.test(tribeId || "") && /^\d{9,10}$/.test(tribe2CustomerId || "");
  const shouldShowAuthCta = !accessToken || isAnonymousUser;

  if (!isValid) {
    return <Navigate to="/404" replace />;
  }

  return (
    <WelcomeScreenBackground>
      <div className="flex min-h-screen flex-col md:px-10">
        <div className="flex flex-grow flex-col px-4 md:flex-row">
          <div className="md:w-[50%] smPlus:mt-20">
            <HeroSection
              textsToAnimate={[t("friendsWithBenefits.welcome.animationText")]}
            />
            <div className="flex flex-col md:max-w-[70%]">
              {isCTA ? (
                <div className="mt-24">
                  <WelcomeInfoSection
                    t={t}
                    referralName={referralName}
                    extended={true}
                  />
                  {isDesktop && (
                    <div className="text-white md:text-black">
                      <p className="my-4">
                        <Trans
                          i18nKey="friendsWithBenefits.welcome.referralCTA2"
                          components={[<strong key="key" />]}
                        />
                      </p>
                      <p className="mt-4">
                        {t("friendsWithBenefits.welcome.referralCTA3")}
                      </p>

                      <h2 className="mb-4 mt-16 text-secondary-100">
                        {t("friendsWithBenefits.welcome.nextSteps")}
                      </h2>
                      <ol className="mb-8 ml-4 list-inside list-decimal">
                        <li>
                          {t("friendsWithBenefits.welcome.steps.register")}
                        </li>
                        <li>{t("friendsWithBenefits.welcome.steps.order")}</li>
                        <li>
                          {t("friendsWithBenefits.welcome.steps.benefit")}
                        </li>
                      </ol>
                      <LearnMoreSection t={t} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-24">
                  <WelcomeInfoSection
                    t={t}
                    referralName={referralName}
                    extended={true}
                  />

                  {isDesktop && (
                    <>
                      <StoriesButton
                        t={t}
                        setShowSlideShow={setShowSlideShow}
                      />
                      <LearnMoreSection t={t} />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-center md:w-[50%]">
            {/* if CTA is clicked, show the register or login form */}
            {isCTA ? (
              <div className="mb-8">
                {!isDesktop && (
                  <p className="my-10 text-white">
                    <Trans
                      i18nKey="friendsWithBenefits.welcome.referralCTA2"
                      components={[<strong key="key" />]}
                    />
                  </p>
                )}
                {/* back button to welcome view */}
                <div className="flex flex-col gap-4 smPlus:mt-12">
                  <button
                    onClick={() => setIsCTA(false)}
                    className="flex w-full max-w-[420px] items-center gap-2 lg:m-auto"
                  >
                    <ArrowLeft />
                    <span className="text-highlight-100 underline">
                      {t("common.buttons.back")}
                    </span>
                  </button>
                  {/* register or login form */}
                  {switchForm ? (
                    <RegisterForm
                      afterRegister={() => {
                        if (tribeId) {
                          handleJoinTribe(tribeId);
                        }
                      }}
                    >
                      <p className="inline text-white">
                        <span className="mr-1">
                          {t("newWelcome.registerForm.accountExists")}
                        </span>
                        <a
                          onClick={() => setSwitchForm(!switchForm)}
                          target="_self"
                          rel="noreferrer"
                          className="inline cursor-pointer font-semibold text-highlight-100 focus-within:underline hover:underline focus:underline focus-visible:underline active:underline"
                        >
                          {t("newWelcome.registerForm.logInNow")}
                        </a>
                      </p>
                    </RegisterForm>
                  ) : (
                    <LogInForm
                      joinTribeOnLogin={() => {
                        if (tribeId) {
                          handleJoinTribe(tribeId);
                        }
                      }}
                    >
                      <p className="inline text-white">
                        <span className="mr-1">
                          {t("newWelcome.logInForm.noAccount")}
                        </span>
                        <a
                          onClick={() => setSwitchForm(!switchForm)}
                          target="_self"
                          rel="noreferrer"
                          className="inline cursor-pointer font-semibold text-highlight-100 focus-within:underline hover:underline focus:underline focus-visible:underline active:underline"
                        >
                          {t("newWelcome.logInForm.registerNow")}
                        </a>
                      </p>
                    </LogInForm>
                  )}
                </div>
                {!isDesktop && <LearnMoreSection t={t} />}
              </div>
            ) : (
              <div className="smPlus:mt-24">
                <WelcomeFriendsWithBenefitsContent
                  tribeDiscount={tribe?.tribeDiscountNext || 0}
                  isCTAClicked={isCTA}
                  onCTAClick={() => {
                    shouldShowAuthCta
                      ? // If user is not logged in as existing customer we show register/login view
                        setIsCTA(!isCTA)
                      : // If user is already logged in as existing customer we join and redirect
                        tribeId && handleJoinTribe(tribeId);
                  }}
                />
                {!isDesktop && (
                  <>
                    <StoriesButton
                      t={t}
                      setShowSlideShow={setShowSlideShow}
                      fullWidth
                    />
                    <LearnMoreSection t={t} />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <WelcomeScreenFooter noClaim={isDesktop} />
      </div>
      {showSlideShow && (
        <SlideShow
          onClose={() => setShowSlideShow(false)}
          onCTAClick={() => {
            shouldShowAuthCta
              ? setIsCTA(!isCTA)
              : tribeId && handleJoinTribe(tribeId);
            setShowSlideShow(false);
          }}
        />
      )}
    </WelcomeScreenBackground>
  );
};
